import React, {useCallback} from 'react';
import {Grid, Box, MenuItem} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import FormSection from "../FormSection";
import {isPropertySelected} from "../config/ValidationSchema";
import BooleQuestion from "../input/BooleQuestion";
import CustomMonthPicker from "../input/CustomMonthPicker";
import AnamnesisSelectbox from "../input/AnamnesisSelectbox";
import EpaTextField from 'components/hmo-textfield/EpaTextField';
import InputDivider from "../input/InputDivider";

const HivForm = ({form, readonly, filters}) => {
    const {t} = useTranslation();
    const lang = useCallback((name) => t(`anamnesis.hiv.${name}`), [t]);

    const getTherapyMedicationOptions = () => {
        switch(form.values?.hivTherapyType) {
            case 'INSTI':
                return [
                    {value: 'B_F_TAF', label: lang('B_F_TAF')},
                    {value: 'DTG_3TC', label: lang('DTG_3TC')},
                    {value: 'DTG_ABC_3TC', label: lang('DTG_ABC_3TC')},
                    {value: 'DTG_RPV', label: lang('DTG_RPV')},
                    {value: 'CAB_RPV', label: lang('CAB_RPV')},
                    {value: 'EVG_C_TAF_FTC', label: lang('EVG_C_TAF_FTC')},
                    {value: 'OTHER', label: lang('OTHER')}
                ];
            case 'PI':
                return [
                    {value: 'DRV_C_TAF_FTC', label: lang('DRV_C_TAF_FTC')},
                    {value: 'OTHER', label: lang('OTHER')}
                ];
            case 'NNRTI':
                return [
                    {value: 'DOR_TDF_3TC', label: lang('DOR_TDF_3TC')},
                    {value: 'RPV_TAF_FTC', label: lang('RPV_TAF_FTC')},
                    {value: 'RPV_TDF_FTC', label: lang('RPV_TDF_FTC')},
                    {value: 'OTHER', label: lang('OTHER')}
                ];
            default:
                return [];
        }
    };

    return (
        <FormSection title={lang("title")}>
            <BooleQuestion
                disabled={readonly}
                form={form}
                fieldName="hasHivDiagnosis"
                question={lang("hasHivDiagnosisQuestion")}
            />

            {isPropertySelected(form, "hasHivDiagnosis") && (
                <Box mb={2}>
                    <Grid container spacing={1}>
                        <Grid item xs={4}>
                            <CustomMonthPicker
                                disabled={readonly}
                                label={lang("hivDiagnosisDate")}
                                value={form.values?.hivDiagnosisDate ?? ""}
                                onChange={(e) => form.setFieldValue("hivDiagnosisDate", e.target.value)}
                            />
                        </Grid>
                    </Grid>

                    <Box mt={2}>
                        <Grid container spacing={1}>
                            <Grid item xs={4}>
                                <EpaTextField
                                    disabled={readonly}
                                    name="hivTherapyCount"
                                    label={lang("therapyCountLabel")}
                                    type="number"
                                    value={form.values?.hivTherapyCount ?? ""}
                                    onChange={form.handleChange}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box mt={2}>
                        <BooleQuestion
                            disabled={readonly}
                            form={form}
                            fieldName="hasHivTherapyFailed"
                            question={lang("hasHivTherapyFailedQuestion")}
                            secondary
                        />
                    </Box>

                    <Box mt={2}>
                        <Grid container spacing={1}>
                            <Grid item xs={4}>
                                <AnamnesisSelectbox
                                    disabled={readonly}
                                    form={form}
                                    fieldName="hivTherapyType"
                                    label={lang("hivTherapyTypeLabel")}
                                    onValueChange={(changeEvent) => {
                                        if (changeEvent.target.value === 'NONE') {
                                            form.setFieldValue("hivTherapyMedication", null);
                                        }
                                    }}
                                >
                                    <MenuItem value="NONE">{lang("NONE")}</MenuItem>
                                    <MenuItem value="INSTI">{lang("INSTI")}</MenuItem>
                                    <MenuItem value="PI">{lang("PI")}</MenuItem>
                                    <MenuItem value="NNRTI">{lang("NNRTI")}</MenuItem>
                                </AnamnesisSelectbox>
                            </Grid>

                            {form.values?.hivTherapyType &&
                                form.values?.hivTherapyType !== 'NONE' && (
                                    <Grid item xs={4}>
                                        <AnamnesisSelectbox
                                            disabled={readonly}
                                            form={form}
                                            fieldName="hivTherapyMedication"
                                            label={lang("hivTherapyMedicationLabel")}
                                        >
                                            {getTherapyMedicationOptions().map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </AnamnesisSelectbox>
                                    </Grid>
                                )}
                        </Grid>
                    </Box>
                </Box>
            )}
        </FormSection>
    );
};

export default HivForm;
